import {
  ROLE_LABELS,
  type AuditAction,
  type AuditEntity,
  type CallOutcome,
  type CallStatus,
  type CampaignStatus,
  type LeadStatus,
  type UserRole,
} from '@fenix/shared';
import {
  CALL_STATUS_LABEL,
  CAMPAIGN_STATUS_LABEL,
  LEAD_STATUS_LABEL,
  OUTCOME_LABEL,
  fmtDateTime,
} from '@/lib/format';

export const ACTION_LABEL: Record<AuditAction, string> = {
  create: 'Criação',
  update: 'Edição',
  delete: 'Exclusão',
  import: 'Importação',
  call: 'Ligação',
  start: 'Início',
  pause: 'Pausa',
  sync: 'Sincronização',
  login: 'Login',
  login_failed: 'Login falhou',
  logout: 'Logout',
  account_locked: 'Conta bloqueada',
  token_reuse: 'Reuso de sessão',
  password_change: 'Troca de senha',
  password_reset: 'Senha redefinida',
  sessions_revoked: 'Sessões encerradas',
};

export const ACTION_VARIANT: Record<AuditAction, string> = {
  create: 'bg-success/15 text-green-700 dark:text-green-300',
  update: 'bg-warning/20 text-amber-700 dark:text-amber-300',
  delete: 'bg-destructive/10 text-destructive',
  import: 'bg-info/15 text-info',
  call: 'bg-info/15 text-info',
  start: 'bg-success/15 text-green-700 dark:text-green-300',
  pause: 'bg-warning/20 text-amber-700 dark:text-amber-300',
  sync: 'bg-secondary text-secondary-foreground',
  login: 'bg-secondary text-secondary-foreground',
  login_failed: 'bg-destructive/10 text-destructive',
  logout: 'bg-muted text-muted-foreground',
  account_locked: 'bg-destructive/15 text-destructive',
  token_reuse: 'bg-destructive/15 text-destructive',
  password_change: 'bg-primary/15 text-primary',
  password_reset: 'bg-primary/15 text-primary',
  sessions_revoked: 'bg-muted text-muted-foreground',
};

export const ENTITY_LABEL: Record<AuditEntity, string> = {
  auth: 'Autenticação',
  user: 'Usuário',
  role: 'Perfil',
  lead: 'Lead',
  brand: 'Marca',
  product: 'Produto',
  script: 'Script',
  campaign: 'Campanha',
  call: 'Chamada',
  email: 'E-mail',
  settings: 'Configurações',
};

export const FIELD_LABEL: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  role: 'Perfil',
  active: 'Ativo',
  mustChangePassword: 'Troca de senha pendente',
  password: 'Senha',
  permissions: 'Permissões',
  reason: 'Motivo',
  lockedUntil: 'Bloqueado até',
  sessions: 'Sessões',
  sessionsRevoked: 'Sessões encerradas',
  ip: 'IP',
  phoneE164: 'Telefone',
  country: 'País',
  state: 'Estado',
  city: 'Cidade',
  address: 'Endereço',
  productCodename: 'Produto',
  sourceSlug: 'Origem',
  affId: 'Afiliado',
  externalId: 'ID externo',
  status: 'Status',
  outcome: 'Resultado',
  durationSec: 'Duração (s)',
  mode: 'Modo',
  callId: 'Chamada',
  provider: 'Provedor',
  language: 'Idioma',
  codename: 'Codename',
  checkoutUrl: 'Link de checkout',
  price: 'Preço',
  currency: 'Moeda',
  offerText: 'Oferta',
  description: 'Descrição',
  voiceId: 'Voz',
  isDefault: 'Padrão do idioma',
  firstMessage: 'Primeira frase',
  systemPrompt: 'Instruções',
  scriptTemplateId: 'Script',
  totalLeads: 'Total de leads',
  filters: 'Filtros',
  totalRows: 'Linhas no arquivo',
  created: 'Criados',
  updated: 'Atualizados',
  skipped: 'Ignorados',
  errors: 'Erros',
  companyName: 'Empresa',
  agentName: 'Nome da atendente',
  callLanguage: 'Idioma das ligações',
  voiceProvider: 'Provedor de voz',
  fromNumber: 'Número de origem',
  timezone: 'Fuso horário',
  callWindowStart: 'Início da janela',
  callWindowEnd: 'Fim da janela',
  maxAttempts: 'Máx. tentativas',
  retryIntervalMin: 'Intervalo entre tentativas (min)',
  maxConcurrentCalls: 'Chamadas simultâneas',
  callsPerMinute: 'Chamadas por minuto',
  defaultOfferText: 'Oferta padrão',
  smsEnabled: 'SMS',
  twilioAccountSid: 'Twilio Account SID',
  twilioApiKeySid: 'Twilio API Key SID',
  twilioMessagingServiceSid: 'Twilio Messaging Service',
  sourceSyncEnabled: 'Importação automática (origem)',
  sourceDbHost: 'Host do banco de origem',
  sourceDbPort: 'Porta do banco de origem',
  sourceDbName: 'Banco de origem',
  sourceDbUser: 'Usuário do banco de origem',
  sourceDbSsl: 'SSL do banco de origem',
  sourceTable: 'Tabela de origem',
  sourceSyncIntervalMin: 'Intervalo de sincronização (min)',
  sourceSyncSince: 'Importar a partir de',
  autopilotEnabled: 'Piloto automático',
  autopilotChannel: 'Canal do piloto',
  autopilotLeadsPerRun: 'Ligações por ciclo',
  autopilotIntervalMin: 'Ciclo do piloto (min)',
  autopilotDelayMin: 'Espera após o abandono (min)',
  autopilotMaxAgeDays: 'Idade máxima do carrinho (dias)',
  autopilotCountries: 'Países do piloto',
  phoneValid: 'Telefone válido',
  retellAgentId: 'Agente Retell',
  retellApiKey: 'API key Retell',
  openaiApiKey: 'API key OpenAI',
  twilioApiKeySecret: 'Secret da API Key Twilio',
  sourceDbPassword: 'Senha do banco de origem',
  trigger: 'Gatilho',
  cursor: 'Cursor',
  ok: 'Sucesso',
  agentId: 'Agente',
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/** Valor legível para um campo do diff. */
export function formatValue(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'boolean') {
    if (field === 'active') return v ? 'Ativo' : 'Inativo';
    return v ? 'Sim' : 'Não';
  }
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v))
    return v.length ? v.map((x) => formatValue(field, x)).join(', ') : '(vazio)';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  if (field === 'role' && s in ROLE_LABELS) return ROLE_LABELS[s as UserRole];
  if (field === 'status') {
    return (
      (LEAD_STATUS_LABEL as Record<string, string>)[s as LeadStatus] ??
      (CALL_STATUS_LABEL as Record<string, string>)[s as CallStatus] ??
      (CAMPAIGN_STATUS_LABEL as Record<string, string>)[s as CampaignStatus] ??
      s
    );
  }
  if (field === 'outcome') return (OUTCOME_LABEL as Record<string, string>)[s as CallOutcome] ?? s;
  if (ISO_DATE.test(s)) return fmtDateTime(s);
  return s;
}
